import { Box, Button, Stack } from "@mui/material";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import ClearIcon from "@mui/icons-material/CloseOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AssetsPicker from "./AssetsPicker";
import { tokens } from "../theme";

/**
 * A picked image, shown at the size it will be judged at, with the picker
 * behind it.
 *
 * One component rather than a copy per surface: the channel logo, the channel
 * poster and a playlist item's asset logo are the same control with different
 * dimensions, and three near-identical plates would drift the moment one of
 * them grew a clear button.
 *
 * `contain` rather than `cover` on purpose - a logo cropped to fill its box is
 * a logo you have misjudged.
 */
export default function ImagePlate({
  value,
  onChange,
  height,
  width,
  buttonLabel,
  clearable = true,
  testId,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  height: number;
  width?: number;
  buttonLabel: string;
  /** Offer a way back to empty. Off where absence is not a valid state. */
  clearable?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Stack spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
      <Box sx={{ position: "relative", width: width ?? "100%" }}>
        <Box
          sx={{
            width: "100%",
            height,
            borderRadius: 2,
            border: `1px solid ${tokens.divider}`,
            bgcolor: value ? "transparent" : tokens.canvas,
            backgroundImage: value ? `url(${value})` : undefined,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
          data-testid={testId}
        />
        {clearable && value && (
          <Button
            size="small"
            aria-label={t("imagePlate.removeImage")}
            onClick={() => onChange(null)}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              minWidth: 0,
              minHeight: 0,
              p: 0.5,
              bgcolor: tokens.surface,
              border: `1px solid ${tokens.divider}`,
            }}
          >
            <ClearIcon sx={{ fontSize: 14 }} />
          </Button>
        )}
      </Box>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={() => setOpen(true)}
        sx={{ color: tokens.inkMuted, borderColor: tokens.divider }}
      >
        {buttonLabel}
      </Button>
      <AssetsPicker
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(url) => {
          onChange(url);
          setOpen(false);
        }}
      />
    </Stack>
  );
}
